import type { PresentableTaskResource } from '@timelyapp/integration-cloud-api-spec';
import type { TicLinkedWorkTaskScope } from 'redux/modules/tic/tasks/tic.tasks.scope';
import type { ExternalHourLink } from 'types/hour';
import type { LinkedWorkItem, LinkedWorkItemsQueryParams } from 'types/linkedWorkItem';
import { bootstrappedData } from 'utils/bootstrap';
import { safeHttpHref } from 'utils/url-helpers';

/**
 * URI scheme for native linked work items, shared with the Rails backend: hour read-back
 * emits native external_links entries with this uri. It is never used as INPUT, though —
 * on save the item id travels as `linked_work_item_ids`, and native entries echoed in
 * `external_links` are ignored server-side. The client still mints the uri when an entry
 * arrives without one (deploy skew; see `normalizeIncomingExternalLinks`), and the uri lets
 * native items live in the same uri-keyed task pool as TIC tasks.
 */
export const LINKED_WORK_ITEM_URI_PREFIX = 'timely://work-items/';

/** Tool/provider id for native items; matches the `provider_type` the Rails API returns. */
export const TIMELY_TOOL_ID = 'timely';

export const makeLinkedWorkItemUri = (id: number): string => `${LINKED_WORK_ITEM_URI_PREFIX}${id}`;

export const isLinkedWorkItemUri = (uri: string | null | undefined): boolean =>
  uri?.startsWith(LINKED_WORK_ITEM_URI_PREFIX) ?? false;

/** Returns the linked work item id encoded in a synthetic URI, or null for any other URI. */
export const parseLinkedWorkItemUri = (uri: string | null | undefined): number | null => {
  if (uri == null || !uri.startsWith(LINKED_WORK_ITEM_URI_PREFIX)) {
    return null;
  }
  // Decimal digits only: Number() would also accept hex, exponent and padded
  // forms that Rails reads differently, so a malformed uri must never yield an id.
  const digits = uri.slice(LINKED_WORK_ITEM_URI_PREFIX.length);
  if (!/^\d+$/.test(digits)) {
    return null;
  }
  const id = Number(digits);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

/**
 * Origins an externally supplied image URL must never point at: the page's own origin and the
 * API host. Rendering `<img src>` fires a credentialed GET on render, so an icon URL on either of
 * these (say `/logout`) would act with the user's session just by being displayed.
 */
function ownOrigins(): Array<string> {
  const origins: Array<string> = [];
  const pageOrigin = (globalThis as { location?: Location }).location?.origin;
  if (pageOrigin) origins.push(pageOrigin);
  const apiOrigin = originOf(bootstrappedData.host);
  if (apiOrigin) origins.push(apiOrigin);
  return origins;
}

function originOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * The `icon_url` of a native item as it may be rendered in `<img src>`, or null when it must not
 * be: only absolute http(s) URLs (see `safeHttpHref`) on an origin other than our own qualify.
 * `blockedOrigins` defaults to the page origin and the API host.
 */
export function safeLinkedWorkIconUrl(
  url: string | null | undefined,
  blockedOrigins: ReadonlyArray<string> = ownOrigins()
): string | null {
  const href = safeHttpHref(url);
  if (href == null) return null;
  return blockedOrigins.includes(new URL(href).origin) ? null : href;
}

/**
 * Maps a native linked work item into the `PresentableTaskResource` shape so it can be
 * appended to the shared task pool (`ticTasks` slice) and flow through the hour editor's
 * picker — suggestions, scope filtering, and selected-chip resolution — unchanged.
 *
 * `defaultIconUrl` is injected (rather than imported) so this stays a pure, asset-free
 * mapper; callers pass the Timely logo.
 */
export function linkedWorkItemToPresentableTask(
  item: LinkedWorkItem,
  defaultIconUrl: string | null = null
): PresentableTaskResource {
  const uri = makeLinkedWorkItemUri(item.id);
  const iconUrl = safeLinkedWorkIconUrl(item.icon_url);
  return {
    // The pool merges rows by `id`; the synthetic URI is unique, stable across fetches, and
    // can never collide with TIC's uuid ids.
    id: uri,
    type: 'task',
    external_id: String(item.id),
    uri,
    title: item.title,
    root_uri: null,
    parent_uri: null,
    root_id: null,
    parent_id: null,
    tool_id: TIMELY_TOOL_ID,
    subscription_type: null,
    url_html: item.url,
    // Native items have no TIC API endpoint; the synthetic URI doubles as the canonical id.
    url_api: uri,
    created_at: item.created_at,
    updated_at: item.updated_at,
    start_date: item.start_date,
    end_date: item.due_date,
    completed_at: item.completed_at,
    archived_at: null,
    deleted_at: null,
    metadata: {
      external_type: 'linked_work_item',
      external_code: item.external_code,
      description: item.description,
      icon_url: iconUrl,
      supports_webhooks: false,
      color: null,
    },
    tool: {
      id: TIMELY_TOOL_ID,
      title: item.source_name ?? 'Timely',
      icon_url: iconUrl ?? defaultIconUrl,
    },
    timely_project_id: item.project_id ?? undefined,
  };
}

/**
 * An external_links entry as the API returns it on hour read-back. Native entries
 * (`provider_type: 'timely'` with a `linked_work_item_id`) normally include the
 * `timely://work-items/<id>` uri; `uri` stays optional here so older backends that omit it
 * (deploy skew) are still handled.
 */
export type IncomingExternalHourLink = Omit<ExternalHourLink, 'uri'> &
  Partial<Pick<ExternalHourLink, 'uri'>>;

/**
 * Backfills the `timely://work-items/<id>` uri on incoming native links that lack one
 * (deploy-skew protection — current backends already emit it) so the hour editor's selected
 * chip and task-pool resolution (both keyed by uri) always work. Entries that already carry
 * a uri — all TIC links and server-emitted native links — pass through untouched, making
 * this a no-op/idempotent on fully-formed payloads. Anything other than an array normalizes to no
 * links, so a malformed payload cannot throw inside a reducer or factory.
 */
export function normalizeIncomingExternalLinks(
  links: ReadonlyArray<IncomingExternalHourLink> | null | undefined
): Array<ExternalHourLink> {
  if (!Array.isArray(links) || links.length === 0) {
    return [];
  }
  return links.map(link => {
    if (
      link.uri == null &&
      link.provider_type === TIMELY_TOOL_ID &&
      link.linked_work_item_id != null
    ) {
      return {
        ...link,
        uri: makeLinkedWorkItemUri(link.linked_work_item_id),
        provider_id: link.provider_id ?? link.external_code ?? null,
      };
    }
    return link as ExternalHourLink;
  });
}

/**
 * Splits an hour's links for saving: native links become `linked_work_item_ids`
 * (full-replace semantics — an empty array clears existing native links), while TIC picks
 * keep travelling via `external_links` exactly as before.
 *
 * A link counts as native when it carries the synthetic uri OR a `linked_work_item_id`
 * (defense in depth: a raw, un-normalized read-back entry that slipped past ingestion
 * normalization must never be sent to the backend as a TIC `external_links` entry).
 */
export function splitExternalLinksForSave(links: ReadonlyArray<IncomingExternalHourLink>): {
  externalLinks: Array<ExternalHourLink>;
  linkedWorkItemIds: Array<number>;
} {
  const externalLinks: Array<ExternalHourLink> = [];
  const linkedWorkItemIds: Array<number> = [];
  for (const link of links) {
    const id = parseLinkedWorkItemUri(link.uri) ?? link.linked_work_item_id ?? null;
    if (id != null) {
      linkedWorkItemIds.push(id);
    } else {
      externalLinks.push(link as ExternalHourLink);
    }
  }
  return { externalLinks, linkedWorkItemIds };
}

/**
 * Translates the picker's linked-work scope into query params for the native endpoint, so native
 * items are narrowed by the SERVER exactly like TIC tasks are (TT-5457 moved all narrowing there;
 * re-filtering a fetched page on the client is what this deliberately avoids).
 *
 * - `projectId` whenever the picker is in a project-tied MODE ("this project" or "this integration")
 *   and the entry has a Timely project. The mode is read directly rather than through
 *   `sameProjectIsApplied`/`sameIntegrationIsApplied`: those flags also require the project to be
 *   TIC-linked (a `projectTic` uri or tool), which native-only accounts never have. "This
 *   integration" narrows TIC tasks to the project's tool, and native items belong to no tool — the
 *   project is the only narrowing that means anything for them, and without it another project's
 *   items would leak into a project- or integration-scoped list.
 * - `status` maps the Open/Done toggles onto the endpoint's vocabulary. An omitted status means
 *   both. `'none'` (every toggle off) can't match anything and is handled by the caller skipping
 *   the request outright, so it maps to no param here.
 */
export function resolveNativeLinkedWorkQueryScope(
  scope: TicLinkedWorkTaskScope
): Pick<LinkedWorkItemsQueryParams, 'projectId' | 'status'> {
  const scopedToProject =
    scope.linkedWorkMode === 'project' || scope.linkedWorkMode === 'integration';
  return {
    ...(scopedToProject && scope.selectedProjectId != null
      ? { projectId: scope.selectedProjectId }
      : {}),
    ...(scope.taskQueryScope.status === 'open'
      ? { status: 'open' as const }
      : scope.taskQueryScope.status === 'done'
        ? { status: 'completed' as const }
        : {}),
  };
}
